import { useRef, useCallback } from 'react';

/**
 * Synchronous in-flight guard for submit handlers.
 *
 * Why a ref and not the existing `disabled={loading}` state:
 * `setLoading(true)` only disables the button once React re-renders. A second
 * click that lands inside that window still runs the handler, because the
 * handler's closure sees the stale `loading === false`. The button looks
 * guarded and isn't.
 *
 * A ref is written and read synchronously, so the second call is rejected in
 * the same tick — no render required.
 *
 * Where it matters: create forms whose entity has no unique constraint to fall
 * back on. A double-submitted quotation really does produce two DRAFT rows;
 * company create happens to be saved by its unique-code check, which returns a
 * confusing "code ซ้ำ" error for a row that was in fact created successfully.
 *
 * Usage:
 *   const guard = useSubmitGuard();
 *   const handleSubmit = async (e) => {
 *     e.preventDefault();
 *     if (!validate()) return;          // early-outs must run BEFORE begin()
 *     if (!guard.begin()) return;       // second click lands here and stops
 *     try { await save(); } finally { guard.end(); }
 *   };
 *
 * Keep `disabled={loading}` as well — this guard prevents the duplicate
 * request, the disabled state is what the user sees.
 */
export function useSubmitGuard() {
  const inFlight = useRef(false);

  /** Returns false if a submit is already running (caller should return). */
  const begin = useCallback(() => {
    if (inFlight.current) return false;
    inFlight.current = true;
    return true;
  }, []);

  /** Always call from a `finally` so a thrown error cannot wedge the form. */
  const end = useCallback(() => {
    inFlight.current = false;
  }, []);

  const isInFlight = useCallback(() => inFlight.current, []);

  return { begin, end, isInFlight };
}

export default useSubmitGuard;
